import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";

import { UrlConstantes } from "../utilerias/constantes/urlconstantes";
import {
  DictamenHomologacion,
  HomologacionModelo,
  HomologacionReq,
  HomologacionRes,
} from "../shared/model/iso42001/homologacion-modelo.model";

/**
 * Homologación de Modelos IA (gestión).
 *
 * La carga del archivo va en dos pasos, como la importación FOS-005:
 * previa() lee y valida SIN escribir; cargar() manda el MISMO archivo con la
 * huella de lo que se revisó. El usuario nunca viaja en la petición: el
 * servidor lo toma de la sesión.
 */
@Injectable({ providedIn: "root" })
export class HomologacionModelosService {
  constructor(private http: HttpClient) {}

  /** Los modelos con su situación y su última evaluación, más los KPIs. */
  modelos(): Observable<HomologacionRes> {
    return this.http.post<HomologacionRes>(UrlConstantes.HOMOLOGACION_MODELOS_MODELOS, {});
  }

  /** Activos del inventario y evaluaciones de proveedor, para ligar el modelo. */
  catalogos(): Observable<HomologacionRes> {
    return this.http.post<HomologacionRes>(UrlConstantes.HOMOLOGACION_MODELOS_CATALOGOS, {});
  }

  guardarModelo(modelo: HomologacionModelo): Observable<HomologacionRes> {
    const req: HomologacionReq = { modelo };
    return this.http.post<HomologacionRes>(UrlConstantes.HOMOLOGACION_MODELOS_MODELO_GUARDAR, req);
  }

  bajaModelo(idModelo: number): Observable<HomologacionRes> {
    const req: HomologacionReq = { idModelo };
    return this.http.post<HomologacionRes>(UrlConstantes.HOMOLOGACION_MODELOS_MODELO_BAJA, req);
  }

  evaluaciones(idModelo: number): Observable<HomologacionRes> {
    const req: HomologacionReq = { idModelo };
    return this.http.post<HomologacionRes>(UrlConstantes.HOMOLOGACION_MODELOS_EVALUACIONES, req);
  }

  historial(idModelo: number): Observable<HomologacionRes> {
    const req: HomologacionReq = { idModelo };
    return this.http.post<HomologacionRes>(UrlConstantes.HOMOLOGACION_MODELOS_HISTORIAL, req);
  }

  /**
   * El archivo va con su nombre para que el servidor guarde el original.
   * Sin Content-Type a mano: si se pone, el navegador ya no calcula el
   * boundary y el servidor no encuentra el archivo.
   */
  private formaCarga(archivo: File, idModelo: number, huella?: string, motivo?: string,
    reemplaza?: number | null): FormData {
    const f = new FormData();
    f.append("file", archivo, archivo.name);
    f.append("idModelo", String(idModelo));
    // Contra null y no por verdadero: en la carga la huella es obligatoria,
    // y si viniera vacía hay que mandarla para que el servidor conteste
    // "el archivo cambió" en vez de un 400 sin explicación.
    if (huella != null) {
      f.append("huella", huella);
    }
    if (motivo != null && motivo.trim()) {
      f.append("motivo", motivo.trim());
    }
    // La evaluación (o la ficha) que la vista previa dijo que se reemplazaría: el
    // servidor revisa que siga siendo esa (la huella amarra el archivo; esto, la decisión).
    if (reemplaza != null) {
      f.append("reemplaza", String(reemplaza));
    }
    return f;
  }

  /** Lee y valida el archivo. No escribe nada. */
  previa(archivo: File, idModelo: number): Observable<HomologacionRes> {
    return this.http.post<HomologacionRes>(UrlConstantes.HOMOLOGACION_MODELOS_PREVIA, this.formaCarga(archivo, idModelo));
  }

  /** El MISMO archivo que se revisó, su huella, y el motivo y la evaluación que reemplaza, si reemplaza. */
  cargar(archivo: File, idModelo: number, huella: string, motivo?: string, reemplaza?: number | null)
    : Observable<HomologacionRes> {
    return this.http.post<HomologacionRes>(
      UrlConstantes.HOMOLOGACION_MODELOS_CARGAR,
      this.formaCarga(archivo, idModelo, huella, motivo, reemplaza),
    );
  }

  /* ------------------------ Ficha del catálogo (spec 5.8) ------------------------ */

  /** La ficha del catálogo: la lee y la valida. No escribe nada. */
  fichaPrevia(archivo: File, idModelo: number): Observable<HomologacionRes> {
    return this.http.post<HomologacionRes>(UrlConstantes.HOMOLOGACION_MODELOS_FICHA_PREVIA, this.formaCarga(archivo, idModelo));
  }

  /** La MISMA ficha que se revisó, su huella, y el motivo y la ficha que reemplaza, si reemplaza. */
  fichaCargar(archivo: File, idModelo: number, huella: string, motivo?: string, reemplaza?: number | null)
    : Observable<HomologacionRes> {
    return this.http.post<HomologacionRes>(
      UrlConstantes.HOMOLOGACION_MODELOS_FICHA_CARGAR,
      this.formaCarga(archivo, idModelo, huella, motivo, reemplaza),
    );
  }

  /** Las fichas de un modelo, la vigente primero: solo encabezados. */
  fichas(idModelo: number): Observable<HomologacionRes> {
    const req: HomologacionReq = { idModelo };
    return this.http.post<HomologacionRes>(UrlConstantes.HOMOLOGACION_MODELOS_FICHAS, req);
  }

  /** Una ficha completa, de columnas y tablas hijas: el servidor no lee el archivo. */
  ficha(idFicha: number): Observable<HomologacionRes> {
    const req: HomologacionReq = { idFicha };
    return this.http.post<HomologacionRes>(UrlConstantes.HOMOLOGACION_MODELOS_FICHA, req);
  }

  /** El archivo de la ficha tal cual, como texto, para bajarlo byte por byte. */
  fichaDescarga(idFicha: number): Observable<HomologacionRes> {
    const req: HomologacionReq = { idFicha };
    return this.http.post<HomologacionRes>(UrlConstantes.HOMOLOGACION_MODELOS_FICHA_DESCARGA, req);
  }

  /** Llave autorizar y PIN personal, que valida el servidor. */
  fichaEliminar(idFicha: number, pin: string): Observable<HomologacionRes> {
    const req: HomologacionReq = { idFicha, pin };
    return this.http.post<HomologacionRes>(UrlConstantes.HOMOLOGACION_MODELOS_FICHA_ELIMINAR, req);
  }

  /** Encabezado con sus hijos, el documento parseado y el encabezado de su baseline. */
  detalle(idEval: number): Observable<HomologacionRes> {
    const req: HomologacionReq = { idEval };
    return this.http.post<HomologacionRes>(UrlConstantes.HOMOLOGACION_MODELOS_DETALLE, req);
  }

  /** El archivo tal cual, como texto, para bajarlo byte por byte. */
  descarga(idEval: number): Observable<HomologacionRes> {
    const req: HomologacionReq = { idEval };
    return this.http.post<HomologacionRes>(UrlConstantes.HOMOLOGACION_MODELOS_DESCARGA, req);
  }

  /** Solo una CARGADA: llave autorizar y PIN personal, que valida el servidor. */
  eliminar(idEval: number, pin: string): Observable<HomologacionRes> {
    const req: HomologacionReq = { idEval, pin };
    return this.http.post<HomologacionRes>(UrlConstantes.HOMOLOGACION_MODELOS_ELIMINAR, req);
  }

  /**
   * El dictamen: llave autorizar, firma digital (PNG) y PIN personal.
   * Responde la evaluación firmada y lo que se avisó sin bloquear.
   */
  firmar(datos: {
    idEval: number;
    dictamen: DictamenHomologacion;
    restricciones: string | null;
    comentario: string | null;
    firma: string;
    pin: string;
  }): Observable<HomologacionRes> {
    const req: HomologacionReq = {
      idEval: datos.idEval,
      dictamen: datos.dictamen,
      restricciones: datos.restricciones ?? undefined,
      comentario: datos.comentario ?? undefined,
      firma: datos.firma,
      pin: datos.pin,
    };
    return this.http.post<HomologacionRes>(UrlConstantes.HOMOLOGACION_MODELOS_FIRMAR, req);
  }

  /** Reabre un dictamen firmado: motivo y PIN. Borra la firma; la bitácora guarda cómo estaba. */
  reabrir(idEval: number, motivo: string, pin: string): Observable<HomologacionRes> {
    const req: HomologacionReq = { idEval, motivo, pin };
    return this.http.post<HomologacionRes>(UrlConstantes.HOMOLOGACION_MODELOS_REABRIR, req);
  }
}
